import React, { forwardRef, useMemo } from 'react';
import { createComponentPreviewEntry } from '@wix/editor-elements-integrations';
import type {
  PreviewWrapperProps,
  IComponentPreviewWrapper,
} from '@wix/editor-elements-types/thunderboltPreview';
import type {
  IMenuProps,
  IMenuImperativeActions,
  IMenuPreviewWrapperProps,
} from '../Menu.types';
import { useDropdownPreview } from '../../../common/menu/megaMenuParent';
import { forceOpenIfNeeded } from './previewWrapperUtils';

import { useUpdateDropdownPosition } from './previewWrapperUtils/updateDropdownPosition';
import { MenuPreviewContextProvider } from './MenuPreviewContext';

type IMenuPreviewProps = IMenuProps & {
  forceState?: { selectionSelector: string };
};

function withComponentPreview(
  ViewerComponent: React.ComponentType<IMenuProps>,
): IComponentPreviewWrapper<IMenuPreviewProps, IMenuPreviewWrapperProps> {
  return forwardRef<
    IMenuImperativeActions,
    PreviewWrapperProps<IMenuPreviewProps, IMenuPreviewWrapperProps>
  >((props, ref) => {
    const { items, previewWrapperProps, slots, ...viewerProps } = props;
    const {
      compPreviewState,
      designPanelPreviewStateMap,
      layoutPanelPreviewStateMap,
    } = previewWrapperProps || {};
    const { editSlotId, shownDropdownId, itemId } = useDropdownPreview({
      compPreviewState,
      slots,
      designPanelPreviewStateMap,
      layoutPanelPreviewStateMap,
      items,
    });
    useUpdateDropdownPosition(props.id, shownDropdownId);

    const itemsEnhanced = useMemo(
      () =>
        forceOpenIfNeeded({
          items,
          editSlotId,
          itemId,
        }),
      [items, editSlotId, itemId],
    );

    return (
      <MenuPreviewContextProvider
        designPanelPreviewStateMap={designPanelPreviewStateMap}
      >
        <ViewerComponent
          {...viewerProps}
          slots={slots}
          items={itemsEnhanced}
          ref={ref}
        />
      </MenuPreviewContextProvider>
    );
  });
}

export default (ViewerComponent: React.ComponentType<IMenuPreviewProps>) =>
  createComponentPreviewEntry<IMenuPreviewProps, IMenuPreviewWrapperProps>(
    withComponentPreview(ViewerComponent),
  );
